import * as THREE from 'three';
import type { CarState } from '../../shared/car.js';
import type { Heightfield } from '../../shared/heightfield.js';
import type { BuildingIndex } from '../../shared/collision.js';
import type { ChaseView } from '../graphicsPrefs.js';

/**
 * Where the camera sits for each `ChaseView`, in metres behind and above the car, where it
 * aims, and its lens at rest and at speed.
 *
 * `low` is New York's (mrdoob's slop-newyork): 6.9 m back and 2.1 m up on a 56° lens, aimed
 * half a metre over the road five metres ahead. It is what makes a street look tall. It still
 * widens with speed here, which theirs does not — the widening is how 120 km/h feels like it.
 */
const VIEWS: Record<
  ChaseView,
  { distance: number; height: number; aimHeight: number; lead: number; baseFov: number; maxFov: number }
> = {
  standard: { distance: 9.2, height: 4.1, aimHeight: 1.2, lead: 3.5, baseFov: 62, maxFov: 78 },
  low: { distance: 6.9, height: 2.1, aimHeight: 0.6, lead: 5.2, baseFov: 56, maxFov: 70 },
};

/**
 * How far in front of a wall the lens stops, which has to cover the near plane and the lens's
 * own width at the frame's edges; and the nearest to the car it is ever pulled.
 */
const WALL_MARGIN = 0.8;
const CLOSEST = 2;

/**
 * Chase camera.
 *
 * Two details do most of the work here. First, the camera trails the car's *heading*
 * rather than its velocity, so a drift shows you the side of your own car instead of
 * swinging behind the slide. Second, it pulls back and widens its field of view as speed
 * rises, which is the cheapest way to make 120 km/h feel like 120 km/h.
 */
export class ChaseCamera {
  private readonly position = new THREE.Vector3();
  private readonly target = new THREE.Vector3();
  private currentFov = VIEWS.standard.baseFov;
  private initialised = false;
  private view: (typeof VIEWS)[ChaseView] = VIEWS.standard;

  /** Changes where the camera sits. It glides there rather than cutting. */
  setView(view: ChaseView): void {
    this.view = VIEWS[view];
  }

  private ground: Heightfield | null = null;

  /** Handed the terrain whenever a world loads, so the lens can clear the hill behind. */
  setGround(ground: Heightfield | null): void {
    this.ground = ground;
  }

  private buildings: BuildingIndex | null = null;

  /** The walls the lens must stay on this side of; see `keepOutOfBuildings`. */
  setBuildings(buildings: BuildingIndex | null): void {
    this.buildings = buildings;
  }

  update(camera: THREE.PerspectiveCamera, car: CarState, dt: number, lookBack: boolean): void {
    const heading = car.heading + (lookBack ? Math.PI : 0);
    const fx = -Math.sin(heading);
    const fz = -Math.cos(heading);

    const speed = Math.abs(car.speed);
    const speedT = THREE.MathUtils.clamp(speed / 34, 0, 1);
    const view = this.view;
    const distance = view.distance + speedT * 2.6;
    const height = view.height + speedT * 0.5;

    const desiredX = car.x - fx * distance;
    const desiredZ = car.z - fz * distance;
    // Clearance is kept over whichever is higher: the car, or the ground the camera is
    // actually hanging over nine metres back. Using the car alone puts the lens inside the
    // slope every time you start a climb, because the hill behind you is above you.
    const behindY = this.ground?.heightAt(desiredX, desiredZ) ?? car.y;
    const desiredY = Math.max(car.y, behindY) + height;

    if (!this.initialised) {
      this.position.set(desiredX, desiredY, desiredZ);
      this.target.set(car.x, car.y + view.aimHeight, car.z);
      this.initialised = true;
    }

    // Frame-rate independent exponential smoothing. Looking back snaps, because a
    // smoothed 180° swing is nauseating.
    const lag = lookBack ? 1 : 1 - Math.exp(-9 * dt);
    this.position.x += (desiredX - this.position.x) * lag;
    this.position.y += (desiredY - this.position.y) * lag;
    this.position.z += (desiredZ - this.position.z) * lag;
    this.keepOutOfBuildings(car);

    // Aim a little ahead of the car so you can see into the corner you are entering.
    const leadX = car.x + fx * (view.lead + speedT * 6);
    const leadZ = car.z + fz * (view.lead + speedT * 6);
    const targetLag = 1 - Math.exp(-12 * dt);
    this.target.x += (leadX - this.target.x) * targetLag;
    this.target.y += (car.y + view.aimHeight - this.target.y) * targetLag;
    this.target.z += (leadZ - this.target.z) * targetLag;

    camera.position.copy(this.position);
    camera.lookAt(this.target);

    const desiredFov = view.baseFov + (view.maxFov - view.baseFov) * speedT;
    this.currentFov += (desiredFov - this.currentFov) * (1 - Math.exp(-4 * dt));
    if (Math.abs(camera.fov - this.currentFov) > 0.01) {
      camera.fov = this.currentFov;
      camera.updateProjectionMatrix();
    }
  }

  /**
   * Pulls the lens in towards the car when a wall stands between them.
   *
   * Nine metres back is fine on a boulevard. In Nikiszowiec's lanes the line from the car to
   * the lens runs through the corner just turned, and the frame was a brick slab in front of
   * the car and the black inside of a building around it. The correction moves the smoothed
   * position itself, so it cuts in at once and eases back out on the ordinary lag once the
   * wall is passed.
   */
  private keepOutOfBuildings(car: CarState): void {
    if (!this.buildings) return;
    const dx = this.position.x - car.x;
    const dz = this.position.z - car.z;
    const length = Math.hypot(dx, dz);
    if (length < CLOSEST) return;
    const t = this.buildings.firstWallAlong(car.x, car.z, this.position.x, this.position.z);
    if (t >= 1) return;
    const keep = Math.max(CLOSEST, t * length - WALL_MARGIN) / length;
    this.position.x = car.x + dx * keep;
    this.position.z = car.z + dz * keep;
  }

  /** Drops the smoothing so a respawn or a race start doesn't fly the camera across town. */
  reset(): void {
    this.initialised = false;
  }
}
